import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import moment from 'moment';
import { GetBeacon4SidePolygons } from '../requests/Beacon4PerformanceRequests';

export const loadBeacon4SidePolygons = createAsyncThunk(
	'beacon4Filters/loadSidePolygons',
	async (_, { rejectWithValue }) => {
		try {
			const res = await GetBeacon4SidePolygons();
			return res?.data ?? res;
		} catch (e) {
			return rejectWithValue(e.response?.data ?? e.message);
		}
	}
);

const beacon4FiltersSlice = createSlice({
	name: 'beacon4Filters',
	initialState: {
		module: 'Blended',

		dateFrom:    moment().subtract(1, 'months').format('YYYY-MM-DD'),
		dateTo:      moment().format('YYYY-MM-DD'),
		aggregation: 'Daily',
		side:        '',
		polygon:     '',
		jvid:        '',

		perfTech:            '2G',
		perfMainKpi:         '',
		perfTableKpi:        '',   // KPI shown in the aggregation table; defaults to Main KPI on Apply, then independent
		perfAggregationMode: 'Site',
		adminDialogOpen:     false,

		allPairs: [],
		sides:    [],
		polygons: [],
		allJvids: [],
		jvids:    [],

		visibleSeries: null,
		visibleSeriesLeft:  null,
		visibleSeriesRight: null,
		loading:       false,
		error:         null,
	},
	reducers: {
		setBeacon4Module(state, action) {
			state.module             = action.payload;
			// Reset visibility when switching modules so leftover toggles don't
			// silently filter charts in the other module.
			state.visibleSeries      = null;
			state.visibleSeriesLeft  = null;
			state.visibleSeriesRight = null;
		},

		setBeacon4PerfTech(state, action) {
			state.perfTech     = action.payload;
			state.perfMainKpi  = '';
			state.perfTableKpi = '';   // tech change resets both
		},
		setBeacon4PerfMainKpi(state, action)        { state.perfMainKpi         = action.payload; },
		setBeacon4PerfTableKpi(state, action)       { state.perfTableKpi        = action.payload; },
		setBeacon4PerfAggregationMode(state, action){ state.perfAggregationMode = action.payload; },
		setBeacon4AdminDialog(state, action) { state.adminDialogOpen = action.payload; },

		setBeacon4Aggregation(state, action) { state.aggregation = action.payload; },
		setBeacon4DateFrom(state, action)    { state.dateFrom    = action.payload; },
		setBeacon4DateTo(state, action)      { state.dateTo      = action.payload; },

		setBeacon4Side(state, action) {
			state.side    = action.payload;
			state.polygon = '';
			state.jvid    = '';
			const selectedSides = action.payload
				? action.payload.split(',').map(v => v.trim()).filter(Boolean)
				: [];
			state.polygons = [...new Set(
				state.allPairs
					.filter(p => selectedSides.length === 0 || selectedSides.includes(p.side))
					.map(p => p.polygon)
			)].sort();
			state.jvids = [...new Set(
				state.allPairs
					.filter(p => selectedSides.length === 0 || selectedSides.includes(p.side))
					.map(p => p.jvid ?? p.Jvid)
					.filter(Boolean)
			)].sort();
		},

		setBeacon4Polygon(state, action) {
			state.polygon = action.payload;
			state.jvid    = '';
			const selectedSides    = state.side ? state.side.split(',').map(v => v.trim()).filter(Boolean) : [];
			const selectedPolygons = action.payload ? action.payload.split(',').map(v => v.trim()).filter(Boolean) : [];
			state.jvids = [...new Set(
				state.allPairs
					.filter(p =>
						(selectedSides.length === 0    || selectedSides.includes(p.side)) &&
						(selectedPolygons.length === 0 || selectedPolygons.includes(p.polygon))
					)
					.map(p => p.jvid ?? p.Jvid)
					.filter(Boolean)
			)].sort();
		},

		setBeacon4Jvid(state, action) {
			state.jvid = action.payload;
			const selectedJvids = action.payload
				? action.payload.split(',').map(v => v.trim()).filter(Boolean)
				: [];
			if (selectedJvids.length > 0) {
				const matchingPairs = state.allPairs.filter(p =>
					selectedJvids.includes(p.jvid ?? p.Jvid)
				);
				if (!state.side) {
					state.sides    = [...new Set(matchingPairs.map(p => p.side))].sort();
					state.polygons = [...new Set(matchingPairs.map(p => p.polygon))].sort();
				} else {
					const selectedSides = state.side.split(',').map(v => v.trim()).filter(Boolean);
					state.polygons = [...new Set(
						matchingPairs.filter(p => selectedSides.includes(p.side)).map(p => p.polygon)
					)].sort();
				}
			} else {
				state.sides = [...new Set(state.allPairs.map(p => p.side))].sort();
				if (state.side) {
					const selectedSides = state.side.split(',').map(v => v.trim()).filter(Boolean);
					state.polygons = [...new Set(
						state.allPairs.filter(p => selectedSides.includes(p.side)).map(p => p.polygon)
					)].sort();
				} else {
					state.polygons = [];
				}
			}
		},

		toggleBeacon4Series(state, action) {
			const key = action.payload;
			if (state.visibleSeries === null) {
				state.visibleSeries = [key];
			} else if (state.visibleSeries.includes(key)) {
				const next = state.visibleSeries.filter(k => k !== key);
				state.visibleSeries = next.length > 0 ? next : null;
			} else {
				state.visibleSeries = [...state.visibleSeries, key];
			}
		},
		// Performance — panel-scoped toggle ('left' = KPI Performance, 'right' = KPI Failures)
		toggleBeacon4PanelSeries(state, action) {
			const { panel, key } = action.payload;
			const field = panel === 'right' ? 'visibleSeriesRight' : 'visibleSeriesLeft';
			const current = state[field];
			if (current === null) {
				state[field] = [key];
			} else if (current.includes(key)) {
				const next = current.filter(k => k !== key);
				state[field] = next.length > 0 ? next : null;
			} else {
				state[field] = [...current, key];
			}
		},
		resetBeacon4HiddenSeries(state) {
			state.visibleSeries      = null;
			state.visibleSeriesLeft  = null;
			state.visibleSeriesRight = null;
		},
	},
	extraReducers: (b) => {
		b.addCase(loadBeacon4SidePolygons.pending, (s) => { s.loading = true; s.error = null; })
		 .addCase(loadBeacon4SidePolygons.fulfilled, (s, a) => {
				s.loading  = false;
				s.allPairs = a.payload ?? [];
				s.sides    = [...new Set((a.payload ?? []).map(p => p.side))].sort();
				s.allJvids = [...new Set((a.payload ?? []).map(p => p.jvid ?? p.Jvid).filter(Boolean))].sort();
				if (s.side) {
					const selectedSides = s.side.split(',').map(v => v.trim()).filter(Boolean);
					s.polygons = [...new Set(
						(a.payload ?? []).filter(p => selectedSides.includes(p.side)).map(p => p.polygon)
					)].sort();
					if (s.polygon) {
						const sel = s.polygon.split(',').map(v => v.trim()).filter(Boolean);
						s.jvids = [...new Set(
							(a.payload ?? [])
								.filter(p => selectedSides.includes(p.side) && (sel.length === 0 || sel.includes(p.polygon)))
								.map(p => p.jvid ?? p.Jvid)
								.filter(Boolean)
						)].sort();
					} else {
						s.jvids = [...new Set(
							(a.payload ?? []).filter(p => selectedSides.includes(p.side)).map(p => p.jvid ?? p.Jvid).filter(Boolean)
						)].sort();
					}
				} else {
					s.jvids = s.allJvids;
				}
			})
		 .addCase(loadBeacon4SidePolygons.rejected, (s, a) => { s.loading = false; s.error = a.payload; });
	},
});

export const {
	setBeacon4Module,
	setBeacon4PerfTech, setBeacon4PerfMainKpi, setBeacon4PerfTableKpi, setBeacon4PerfAggregationMode,
	setBeacon4AdminDialog,
	setBeacon4Aggregation,
	setBeacon4DateFrom, setBeacon4DateTo,
	setBeacon4Side, setBeacon4Polygon, setBeacon4Jvid,
	toggleBeacon4Series, toggleBeacon4PanelSeries, resetBeacon4HiddenSeries,
} = beacon4FiltersSlice.actions;

export default beacon4FiltersSlice.reducer;

export const selectBeacon4Filters             = (state) => state.beacon4Filters;
export const selectBeacon4Module              = (state) => state.beacon4Filters.module;
export const selectBeacon4VisibleSeries       = (state) => state.beacon4Filters.visibleSeries;
export const selectBeacon4AllJvids            = (state) => state.beacon4Filters.allJvids;
export const selectBeacon4PerfTech            = (state) => state.beacon4Filters.perfTech;
export const selectBeacon4PerfMainKpi         = (state) => state.beacon4Filters.perfMainKpi;
export const selectBeacon4PerfTableKpi        = (state) => state.beacon4Filters.perfTableKpi;
export const selectBeacon4PerfAggregationMode = (state) => state.beacon4Filters.perfAggregationMode;
export const selectBeacon4AdminDialogOpen = (state) => state.beacon4Filters.adminDialogOpen;
export const selectBeacon4VisibleSeriesLeft  = (state) => state.beacon4Filters.visibleSeriesLeft;
export const selectBeacon4VisibleSeriesRight = (state) => state.beacon4Filters.visibleSeriesRight;